import React, { useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Row,
  Col,
  message,
  Tooltip,
  Space,
  Tag,
} from 'antd';
import { useHistory } from 'react-router-dom';
import {
  PlusOutlined,
  FilePdfOutlined,
  FolderOutlined,
  EditOutlined,
  FolderOpenOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import MyTable from '../helpers/MyTable';
import SubPageLayout from '../helpers/SubPageLayout';
import SubPageHeader from '../helpers/SubPageHeader';
import moment from 'moment';

const { Option } = Select;
const { confirm } = Modal;

const Projects = () => {
  const history = useHistory();
  const [form] = Form.useForm();

  const [projects, setProjects] = useState([
    {
      code: 'PRJ-001',
      quoteNumber: 'MAQ1001',
      companyName: 'Apex Mining Ltd',
      siteName: 'North Ridge Site',
      completionDate: '2025-03-15',
      businessManager: 'Sarah Johnson',
      createdBy: 'Jean-Beni',
      createdOn: '2024-11-01',
      projectStatus: 'In Progress',
      poDocumentName: 'MAQ1001.pdf',
      poDocumentUrl: '/docs/MAQ1001.pdf',
    },
    {
      code: 'PRJ-002',
      quoteNumber: 'MAQ1002',
      companyName: 'Global Tunneling Co.',
      siteName: 'Metro Tunnel Phase 2',
      completionDate: '2025-06-30',
      businessManager: 'Michael Chen',
      createdBy: 'Admin',
      createdOn: '2024-11-05',
      projectStatus: 'Planned',
      poDocumentName: 'MAQ1002.pdf',
      poDocumentUrl: '/docs/MAQ1002.pdf',
    },
    {
      code: 'PRJ-003',
      quoteNumber: 'MAQ1003',
      companyName: 'CoalWorks Pty Ltd',
      siteName: 'Blackstone Mine',
      completionDate: '2024-12-01',
      businessManager: 'Emily Davis',
      createdBy: 'Jean-Beni',
      createdOn: '2024-10-20',
      projectStatus: 'Completed',
      poDocumentName: 'MAQ1003.pdf',
      poDocumentUrl: '/docs/MAQ1003.pdf',
    },
    {
      code: 'PRJ-004',
      quoteNumber: 'MAQ1004',
      companyName: 'SafeChem Industries',
      siteName: 'Plant 7 Expansion',
      completionDate: '2025-01-20',
      businessManager: 'Carlos Martinez',
      createdBy: 'System',
      createdOn: '2024-11-10',
      projectStatus: 'In Progress',
      poDocumentName: 'MAQ1004.pdf',
      poDocumentUrl: '/docs/MAQ1004.pdf',
    },
    {
      code: 'PRJ-005',
      quoteNumber: 'MAQ1005',
      companyName: 'StormShield Corp.',
      siteName: 'Coastal Shelter Project',
      completionDate: '2025-09-10',
      businessManager: 'Olivia Brown',
      createdBy: 'Jean-Beni',
      createdOn: '2024-11-15',
      projectStatus: 'Planned',
      poDocumentName: 'MAQ1005.pdf',
      poDocumentUrl: '/docs/MAQ1005.pdf',
    },
  ]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [poFileList, setPoFileList] = useState([]);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingProjectCode, setEditingProjectCode] = useState(null);

  // 🔍 search state
  const [searchQuery, setSearchQuery] = useState('');

  // Details modal state
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [detailsProject, setDetailsProject] = useState(null);

  // Demo dropdown data – later you can load from API
  const companyOptions = [
    'Apex Mining Ltd',
    'Global Tunneling Co.',
    'CoalWorks Pty Ltd',
    'SafeChem Industries',
    'StormShield Corp.',
  ];

  const siteOptions = [
    'North Ridge Site',
    'Metro Tunnel Phase 2',
    'Blackstone Mine',
    'Plant 7 Expansion',
    'Coastal Shelter Project',
  ];

  const businessManagers = [
    'Sarah Johnson',
    'Michael Chen',
    'Emily Davis',
    'Carlos Martinez',
    'Olivia Brown',
  ];

  const renderStatusTag = (status) => {
    let color = 'default';
    if (status === 'Completed') color = 'green';
    else if (status === 'In Progress') color = 'blue';
    else if (status === 'Planned') color = 'gold';
    return <Tag color={color}>{status}</Tag>;
  };

  // ---------- TABLE COLUMNS ----------

  const columns = [
    {
      title: '',
      dataIndex: 'folder',
      key: 'folder',
      width: 48,
      align: 'center',
      render: () => (
        <FolderOutlined style={{ fontSize: 18, color: '#4b5563' }} />
      ),
    },
    {
      title: 'Quote Number',
      dataIndex: 'quoteNumber',
      key: 'quoteNumber',
    },
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: 'Site Name',
      dataIndex: 'siteName',
      key: 'siteName',
    },
    {
      title: 'Completion Date',
      dataIndex: 'completionDate',
      key: 'completionDate',
    },
    {
      title: 'Business Manager',
      dataIndex: 'businessManager',
      key: 'businessManager',
    },

    // Document column with PDF icon link
    {
      title: 'Document',
      dataIndex: 'poDocumentUrl',
      key: 'document',
      align: 'center',
      render: (url, record) => {
        const hasDoc = !!url;
        const icon = (
          <FilePdfOutlined
            style={{
              fontSize: 20,
              color: hasDoc ? '#ef4444' : '#d1d5db',
            }}
          />
        );

        const label = hasDoc
          ? record.poDocumentName || 'Download PO (PDF)'
          : 'No document';

        if (!hasDoc) {
          return (
            <Tooltip title={label}>
              <span
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'not-allowed', opacity: 0.6 }}
              >
                {icon}
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={label}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {icon}
            </a>
          </Tooltip>
        );
      },
    },

    // Actions column
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const iconStyle = {
          fontSize: 18,
          color: '#6b7280',
          cursor: 'pointer',
        };

        return (
          <Space size="middle" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Edit order">
              <EditOutlined
                style={iconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(record);
                }}
              />
            </Tooltip>

            <Tooltip title="Open order products">
              <FolderOpenOutlined
                style={iconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenProject(record.code);
                }}
              />
            </Tooltip>

            <Tooltip title="Order details">
              <InfoCircleOutlined
                style={iconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDetailsClick(record);
                }}
              />
            </Tooltip>

            <Tooltip title="Delete order">
              <DeleteOutlined
                style={{ ...iconStyle, color: '#ef4444' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteProject(record.code);
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // ---------- FILTERED DATA ----------

  const filteredProjects = projects.filter((p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (p.code || '').toLowerCase().includes(q) ||
      (p.quoteNumber || '').toLowerCase().includes(q) ||
      (p.companyName || '').toLowerCase().includes(q) ||
      (p.siteName || '').toLowerCase().includes(q)
    );
  });

  // ---------- HELPERS ----------

  const generateNextProjectCode = () => {
    if (!projects.length) return 'PRJ-001';
    const nums = projects
      .map((p) => p.code)
      .filter((c) => /^PRJ-\d{3}$/i.test(c))
      .map((c) => parseInt(c.slice(4), 10));
    const max = nums.length ? Math.max(...nums) : 0;
    const next = (max + 1).toString().padStart(3, '0');
    return `PRJ-${next}`;
  };

  // ---------- MODAL HANDLERS ----------

  const resetModalState = () => {
    setEditingProjectCode(null);
    setModalMode('create');
    form.resetFields();
    setPoFileList([]);
  };

  const showCreateModal = () => {
    resetModalState();
    setModalMode('create');
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    resetModalState();
  };

  const handlePoBeforeUpload = (file) => {
    setPoFileList([file]); // keep only one file
    return false; // prevent auto upload
  };

  const handleRemoveFile = () => {
    setPoFileList([]);
  };

  const handleOpenProject = (code) => {
    history.push(`/projects/${code}/products`);
  };

  const handleEditClick = (record) => {
    setModalMode('edit');
    setEditingProjectCode(record.code);

    form.setFieldsValue({
      // no code field here anymore
      quoteNumber: record.quoteNumber,
      companyName: record.companyName,
      siteName: record.siteName,
      completionDate: record.completionDate
        ? moment(record.completionDate, 'YYYY-MM-DD')
        : null,
      businessManager: record.businessManager,
    });

    setPoFileList([]);
    setIsModalVisible(true);
  };

  const handleDeleteProject = (code) => {
    confirm({
      title: 'Delete this order?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This action cannot be undone. Are you sure you want to remove this order?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        setProjects((prev) => prev.filter((p) => p.code !== code));
        message.success('Order deleted');
      },
    });
  };

  const handleDetailsClick = (record) => {
    setDetailsProject(record);
    setIsDetailsVisible(true);
  };

  const handleSubmit = (values) => {
    const completionDate = values.completionDate
      ? values.completionDate.format('YYYY-MM-DD')
      : '';

    const poDocName =
      poFileList[0]?.name || `${values.quoteNumber || 'MAQ'}.pdf`;

    const poDocUrl = poFileList[0]
      ? URL.createObjectURL(poFileList[0])
      : undefined; // in real app you'll get URL from backend

    if (modalMode === 'edit' && editingProjectCode) {
      setProjects((prev) =>
        prev.map((p) =>
          p.code === editingProjectCode
            ? {
                ...p,
                // code stays the same – no editing in the modal
                quoteNumber: values.quoteNumber,
                companyName: values.companyName,
                siteName: values.siteName,
                completionDate,
                businessManager: values.businessManager,
                poDocumentName: poDocName || p.poDocumentName,
                poDocumentUrl: poDocUrl || p.poDocumentUrl,
              }
            : p
        )
      );
      message.success('Order updated successfully');
    } else {
      const newCode = generateNextProjectCode();

      const newProject = {
        code: newCode,
        quoteNumber: values.quoteNumber,
        companyName: values.companyName,
        siteName: values.siteName,
        completionDate,
        businessManager: values.businessManager,
        createdBy: 'Current User',
        createdOn: moment().format('YYYY-MM-DD'),
        projectStatus: 'Planned',
        poDocumentName: poDocName,
        poDocumentUrl: poDocUrl,
      };

      setProjects((prev) => [newProject, ...prev]);
      message.success('Order created successfully');
    }

    handleModalCancel();
  };

  const isEditMode = modalMode === 'edit';

  return (
    <SubPageLayout
      title="Orders"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
          Create New Order
        </Button>
      }
    >
      <SubPageHeader
        searchLoading={false}
        searchPlaceholder="Search Orders"
        onSearch={(value) => setSearchQuery(value)}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <MyTable
        columns={columns}
        dataSource={filteredProjects}
        rowKey="code"
        onRow={(record) => ({
          onClick: () => handleOpenProject(record.code),
          style: { cursor: 'pointer' },
        })}
      />

      {/* 🌟 Create / Edit Order Modal */}
      <Modal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        title={null}
        footer={null}
        centered
        width={720}
        destroyOnClose
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background:
              'linear-gradient(135deg, #ffffff 0%, #f3f4ff 40%, #eef7ff 100%)',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
          }}
        >
          <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              {isEditMode ? 'Edit Order' : 'Create New Order'}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              {isEditMode
                ? 'Update the order details and purchase order / quote document.'
                : 'Capture key order details and attach the purchase order / quote document.'}
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark="optional"
          >
            {/* Quote Number full-width row to keep layout nice after removing Project Code */}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Quote Number"
                  name="quoteNumber"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter the quote number',
                    },
                    {
                      pattern: /^MAQ\d+$/i,
                      message: 'Quote Number must start with MAQ, e.g. MAQ1006',
                    },
                  ]}
                >
                  <Input placeholder="e.g. MAQ1006" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Company Name"
                  name="companyName"
                  rules={[
                    { required: true, message: 'Please select a company' },
                  ]}
                >
                  <Select
                    placeholder="Select company"
                    showSearch
                    optionFilterProp="children"
                  >
                    {companyOptions.map((c) => (
                      <Option key={c} value={c}>
                        {c}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Site Name"
                  name="siteName"
                  rules={[
                    { required: true, message: 'Please select a site' },
                  ]}
                >
                  <Select
                    placeholder="Select site"
                    showSearch
                    optionFilterProp="children"
                  >
                    {siteOptions.map((s) => (
                      <Option key={s} value={s}>
                        {s}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Completion Date"
                  name="completionDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a completion date',
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Select completion date"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Business Manager"
                  name="businessManager"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a business manager',
                    },
                  ]}
                >
                  <Select
                    placeholder="Select manager"
                    showSearch
                    optionFilterProp="children"
                  >
                    {businessManagers.map((bm) => (
                      <Option key={bm} value={bm}>
                        {bm}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Purchase Order / Quote Document (PDF)"
              name="purchaseOrderDocument"
            >
              <Upload.Dragger
                multiple={false}
                fileList={poFileList}
                beforeUpload={handlePoBeforeUpload}
                onRemove={handleRemoveFile}
                accept="application/pdf"
                style={{
                  borderRadius: 12,
                  background: '#f9fafb',
                }}
              >
                <p className="ant-upload-drag-icon">
                  <FilePdfOutlined style={{ fontSize: 32, color: '#ef4444' }} />
                </p>
                <p className="ant-upload-text">
                  Click or drag PDF to this area to upload
                </p>
                <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                  Only PDF files are supported. Attach the official purchase
                  order / quote document for this order.
                </p>
              </Upload.Dragger>
            </Form.Item>

            <div
              style={{
                marginTop: 16,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <Button onClick={handleModalCancel}>Cancel</Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => form.submit()}
              >
                {isEditMode ? 'Save Changes' : 'Create Order'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* 🔍 Order Details Modal */}
      <Modal
        visible={isDetailsVisible}
        onCancel={() => setIsDetailsVisible(false)}
        footer={null}
        centered
        width={640}
        title={null}
        destroyOnClose
      >
        {detailsProject && (
          <div
            style={{
              padding: 24,
              borderRadius: 16,
              background: '#ffffff',
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  {detailsProject.code} &mdash; {detailsProject.companyName}
                </span>
                {renderStatusTag(detailsProject.projectStatus || 'Planned')}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                {detailsProject.siteName}
              </div>
            </div>

            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Code
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.code}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Quote Number
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.quoteNumber}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Company
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.companyName}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Site
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.siteName}
                  </div>
                </div>
              </Col>

              <Col span={12}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Completion Date
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.completionDate}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Business Manager
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.businessManager}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Created By
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.createdBy || '—'}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                    Created On
                  </div>
                  <div style={{ fontSize: 14, color: '#111827' }}>
                    {detailsProject.createdOn || '—'}
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>
                Purchase Order / Quote Document
              </div>
              <div style={{ marginTop: 4 }}>
                {detailsProject.poDocumentUrl ? (
                  <a
                    href={detailsProject.poDocumentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <FilePdfOutlined style={{ fontSize: 18, color: '#ef4444' }} />
                    <span style={{ fontSize: 13 }}>
                      {detailsProject.poDocumentName || 'Download PDF'}
                    </span>
                  </a>
                ) : (
                  <span style={{ fontSize: 13, color: '#9ca3af' }}>
                    No document attached
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </SubPageLayout>
  );
};

export default Projects;
